import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const MENU_SECTIONS = [
  {
    labelKey: 'nav.menu.performance',
    fallback: 'Performance',
    emoji: '📊',
    items: [
      { labelKey: 'nav.creators', fallback: 'Mes Créateurs', path: '/manager/creators', emoji: '👤' },
      { labelKey: 'nav.performance', fallback: 'Performance', path: '/manager/performance', emoji: '📈' },
      { labelKey: 'nav.history', fallback: 'Historique', path: '/manager/performances', emoji: '📅' },
      { labelKey: 'nav.sessions', fallback: 'Sessions LIVE', path: '/manager/sessions', emoji: '🔴' },
      { labelKey: 'nav.violations', fallback: 'Violations', path: '/manager/violations', emoji: '⚠️' },
    ],
  },
  {
    labelKey: 'nav.menu.competition',
    fallback: 'Compétition',
    emoji: '🏆',
    items: [
      { labelKey: 'nav.recruitment', fallback: 'Recrutement', path: '/manager/recruitment', emoji: '🎯' },
      { labelKey: 'nav.matchOff', fallback: 'Match OFF', path: '/manager/events', emoji: '⚔️' },
      { labelKey: 'nav.tournaments', fallback: 'Tournois', path: '/manager/tournaments', emoji: '🏆' },
    ],
  },
  {
    labelKey: 'nav.menu.mySpace',
    fallback: 'Mon espace',
    emoji: '👤',
    items: [
      { labelKey: 'nav.payments', fallback: 'Ma Caisse', path: '/manager/payments', emoji: '💰' },
      { labelKey: 'nav.notifications', fallback: 'Notifications', path: '/manager/notifications', emoji: '🔔' },
      { labelKey: 'nav.profile', fallback: 'Profil', path: '/manager/profile', emoji: '👤' },
      { labelKey: 'nav.region', fallback: 'Vue Régionale', path: '/manager/region-performance', emoji: '🌍', roleRequired: 'region_manager' },
      { labelKey: 'nav.cashback', fallback: 'Paiements Cashback', path: '/manager/cashback-payments', emoji: '💸', requiresPaid: true },
    ],
  },
  {
    labelKey: 'nav.menu.tools',
    fallback: 'Outils',
    emoji: '🛠️',
    items: [
      { labelKey: 'nav.ai', fallback: 'IA', path: '/manager/ai', emoji: '✨' },
      { labelKey: 'nav.tools_poster', fallback: 'Affiche', path: '/manager/tools/poster', emoji: '🖼️' },
    ],
  },
];

const ManagerBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profile, setProfile] = useState(null);

  const isActive = (path) => location?.pathname === path;

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) return;
        const { data } = await supabase?.from('users')?.select('role, is_paid')?.eq('id', user.id)?.single();
        if (data) setProfile(data);
      } catch {}
    })();
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await supabase?.auth?.signOut();
    navigate('/login-authentication');
  };

  const dashboardActive = isActive('/manager/dashboard');

  return (
    <>
      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 md:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 98 }}
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Slide-up menu */}
      <div
        className="fixed left-0 right-0 md:hidden transition-transform duration-300"
        style={{
          bottom: '64px',
          maxHeight: '75vh',
          overflowY: 'auto',
          backgroundColor: '#111111',
          borderTop: '1px solid #2a2a2a',
          borderRadius: '16px 16px 0 0',
          zIndex: 99,
          transform: menuOpen ? 'translateY(0)' : 'translateY(100%)',
        }}
      >
        <div className="p-4 space-y-3">
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl transition-colors"
            style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600 }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {t('nav.logout', 'Déconnexion')}
          </button>

          {MENU_SECTIONS.map((section) => (
            <div key={section.labelKey}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <span style={{ fontSize: 14 }}>{section.emoji}</span>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Sora, sans-serif' }}>
                  {t(section.labelKey, section.fallback)}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {section.items.filter(item => {
                  if (item.roleRequired && profile?.role !== item.roleRequired) return false;
                  if (item.requiresPaid && !profile?.is_paid) return false;
                  return true;
                }).map((item) => {
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all"
                      style={{
                        backgroundColor: active ? 'rgba(229,32,42,0.1)' : 'rgba(255,255,255,0.03)',
                        border: active ? '1px solid rgba(229,32,42,0.3)' : '1px solid transparent',
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{item.emoji}</span>
                      <span className="text-xs text-center" style={{ color: active ? '#E5202A' : 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif', fontWeight: active ? 600 : 400, lineHeight: 1.2 }}>
                        {t(item.labelKey, item.fallback)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 md:hidden flex items-center"
        style={{ height: '64px', backgroundColor: '#0a0a0a', borderTop: '1px solid #1a1a1a', zIndex: 100 }}
      >
        {/* Left: Sessions LIVE */}
        <button
          onClick={() => navigate('/manager/sessions')}
          className="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
          style={{ color: isActive('/manager/sessions') ? '#E5202A' : 'rgba(255,255,255,0.35)', background: 'none', border: 'none' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></svg>
          <span style={{ fontSize: '9px', fontFamily: 'Inter, sans-serif', fontWeight: isActive('/manager/sessions') ? 600 : 400 }}>
            {t('nav.sessions', 'Sessions')}
          </span>
        </button>

        {/* Left: Créateurs */}
        <button
          onClick={() => navigate('/manager/creators')}
          className="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
          style={{ color: isActive('/manager/creators') ? '#E5202A' : 'rgba(255,255,255,0.35)', background: 'none', border: 'none' }}
        >
          <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M2 16c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: '9px', fontFamily: 'Inter, sans-serif', fontWeight: isActive('/manager/creators') ? 600 : 400 }}>
            {t('nav.creators', 'Créateurs')}
          </span>
        </button>

        {/* Center: Dashboard (elevated) */}
        <div className="flex flex-col items-center justify-center flex-1 h-full" style={{ position: 'relative', top: '-8px' }}>
          <button onClick={() => navigate('/manager/dashboard')} className="flex flex-col items-center justify-center gap-0.5" style={{ background: 'none', border: 'none' }}>
            <div className="flex items-center justify-center rounded-full" style={{
              width: '44px', height: '44px',
              background: dashboardActive ? 'linear-gradient(135deg, #E5202A, #cc0000)' : 'linear-gradient(135deg, #1a1a1a, #2a2a2a)',
              border: dashboardActive ? '2px solid #E5202A' : '2px solid #333',
            }}>
              <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
                <rect x="1" y="1" width="7" height="7" rx="1.5" stroke="white" strokeWidth="1.5" />
                <rect x="10" y="1" width="7" height="7" rx="1.5" stroke="white" strokeWidth="1.5" />
                <rect x="1" y="10" width="7" height="7" rx="1.5" stroke="white" strokeWidth="1.5" />
                <rect x="10" y="10" width="7" height="7" rx="1.5" stroke="white" strokeWidth="1.5" />
              </svg>
            </div>
            <span style={{ fontSize: '9px', fontFamily: 'Inter, sans-serif', color: dashboardActive ? '#E5202A' : 'rgba(255,255,255,0.35)', fontWeight: dashboardActive ? 600 : 400 }}>
              {t('nav.dashboard', 'Dashboard')}
            </span>
          </button>
        </div>

        {/* Right: Performance */}
        <button
          onClick={() => navigate('/manager/performance')}
          className="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
          style={{ color: isActive('/manager/performance') ? '#E5202A' : 'rgba(255,255,255,0.35)', background: 'none', border: 'none' }}
        >
          <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M9 5v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontSize: '9px', fontFamily: 'Inter, sans-serif', fontWeight: isActive('/manager/performance') ? 600 : 400 }}>
            {t('nav.performance', 'Perf.')}
          </span>
        </button>

        {/* Right: Menu hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
          style={{ color: menuOpen ? '#E5202A' : 'rgba(255,255,255,0.35)', background: 'none', border: 'none' }}
        >
          <div className="flex flex-col gap-1">
            <span className="block w-4 h-0.5 rounded-full transition-all" style={{ backgroundColor: menuOpen ? '#E5202A' : 'rgba(255,255,255,0.5)', transform: menuOpen ? 'rotate(45deg) translateY(3px)' : 'none' }} />
            <span className="block w-4 h-0.5 rounded-full transition-all" style={{ backgroundColor: menuOpen ? 'transparent' : 'rgba(255,255,255,0.5)' }} />
            <span className="block w-4 h-0.5 rounded-full transition-all" style={{ backgroundColor: menuOpen ? '#E5202A' : 'rgba(255,255,255,0.5)', transform: menuOpen ? 'rotate(-45deg) translateY(-3px)' : 'none' }} />
          </div>
          <span style={{ fontSize: '9px', fontFamily: 'Inter, sans-serif', fontWeight: menuOpen ? 600 : 400 }}>
            {t('common.menu')}
          </span>
        </button>
      </nav>
    </>
  );
};

export default ManagerBottomNav;
